<div class="min-h-screen flex items-center justify-center bg-center bg-no-repeat page-bg">
  <div class="card max-w-[400px] w-full shadow-lg">
    <form [formGroup]="codeForm" (ngSubmit)="onSubmit()" class="card-body flex flex-col gap-5 p-10" novalidate>
      <!-- Header based on verification method -->
      <div class="text-center">
        <div class="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-light">
          <i class="ki-filled text-2xl text-primary"
             [class.ki-message-text-2]="verificationMethod() === 'sms'"
             [class.ki-phone]="verificationMethod() === 'call'"
             [class.ki-sms]="verificationMethod() === 'email'"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 leading-none mb-2.5">
          @switch (verificationMethod()) {
            @case ('call') { Voice Call Verification }
            @case ('email') { Email Verification }
            @default { Text Message Verification }
          }
        </h3>
        @if (isSending && sendingChannel === verificationMethod()) {
          <span class="block text-2sm text-gray-600 font-medium">Sending your code…</span>
        } @else if (verificationMethod() === 'email' ? maskedEmail : maskedPhone) {
          <span class="block text-2sm text-gray-600 font-medium mb-1">
            @switch (verificationMethod()) {
              @case ('call') { Enter the code from the voice call to }
              @case ('email') { Enter the code we emailed to }
              @default { Enter the code we texted to }
            }
          </span>
          <p class="font-bold text-sm text-gray-800">
            {{ verificationMethod() === 'email' ? maskedEmail : maskedPhone }}
          </p>
        }
      </div>

      <!-- Send failure banner: keeps the reason on screen instead of relying on a toast -->
      @if (sendError) {
        <div class="alert alert-danger text-xs" role="alert">
          {{ sendError }}
        </div>
      }

      <!-- Too many attempts -->
      @if (isLocked) {
        <div class="alert alert-warning text-xs" role="alert">
          Too many incorrect codes. Please wait {{ lockSecondsLeft }}s before trying again.
        </div>
      }

      <!-- Code input -->
      <div class="flex flex-col gap-2">
        <input
          id="code_one"
          type="text"
          class="input w-full text-center text-lg tracking-[0.3em] font-semibold"
          placeholder="Enter code"
          formControlName="token"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="10"
          autofocus
          aria-label="Verification code"
          (input)="onCodeInput()" />

        <!-- Resend for the active method only -->
        <div class="flex items-center justify-center gap-1 text-xs text-gray-600" aria-live="polite">
          <span>Didn't get it?</span>
          @if (isSending && sendingChannel === verificationMethod()) {
            <span class="text-gray-500">Sending…</span>
          } @else if (countdownValue > 0) {
            <span class="text-gray-500">
              @switch (verificationMethod()) {
                @case ('call') { Call again in }
                @case ('email') { Resend email in }
                @default { Resend text in }
              }
              <span class="font-semibold tabular-nums">{{ countdownValue }}s</span>
            </span>
          } @else {
            <button type="button" class="link font-semibold" (click)="onResendCode()" [disabled]="isSending">
              @switch (verificationMethod()) {
                @case ('call') { Call again }
                @case ('email') { Resend email }
                @default { Resend text }
              }
            </button>
          }
        </div>
      </div>

      <!-- Submit Button -->
      <button type="submit" class="btn btn-primary flex justify-center items-center"
              [disabled]="codeForm.invalid || isVerifying || isLocked">
        @if (isVerifying) {
          <i class="ki-spin ki-spinner mr-2"></i> Verifying…
        } @else {
          Continue <i class="ki-filled ki-black-right ml-2"></i>
        }
      </button>

      <!-- Method picker: only the method just used is on cooldown -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 text-2xs uppercase tracking-wide text-gray-500">
          <span class="h-px flex-1 bg-gray-200"></span>
          Or get a code by
          <span class="h-px flex-1 bg-gray-200"></span>
        </div>
        <div class="grid grid-cols-3 gap-2">
          @for (ch of channels; track ch) {
            @if (hasChannel(ch)) {
              <button
                type="button"
                class="method-btn"
                [class.active]="verificationMethod() === ch"
                [disabled]="!canSend(ch)"
                (click)="onSelectChannel(ch)"
                [attr.aria-label]="ch === 'sms' ? 'Send a text message' : ch === 'call' ? 'Call me' : 'Send an email'">
                @if (isSending && sendingChannel === ch) {
                  <i class="ki-spin ki-spinner text-lg"></i>
                } @else {
                  <i class="ki-filled text-lg"
                     [class.ki-message-text-2]="ch === 'sms'"
                     [class.ki-phone]="ch === 'call'"
                     [class.ki-sms]="ch === 'email'"></i>
                }
                <span class="font-medium">
                  @switch (ch) {
                    @case ('call') { Call }
                    @case ('email') { Email }
                    @default { Text }
                  }
                </span>
                @if (cooldownLeft[ch] > 0) {
                  <span class="text-2xs text-gray-500 tabular-nums">{{ cooldownLeft[ch] }}s</span>
                }
              </button>
            }
          }
        </div>
      </div>

      <div class="flex items-center justify-between gap-3">
        <label class="checkbox-group items-start">
          <input type="checkbox" formControlName="rememberUser" class="checkbox checkbox-sm mt-0.5" />
          <span class="flex flex-col">
            <span class="checkbox-label">Remember this browser</span>
            <span class="text-2xs text-gray-500">Don't ask for a code here for 45 days</span>
          </span>
        </label>
        <button type="button" class="btn btn-sm btn-light" (click)="onLogout()">
          Log out
        </button>
      </div>

    </form>
  </div>
  
  <!-- Mobile Number Registration Prompt Modal -->
  @if (showMobilePrompt) {
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div class="card max-w-md w-full shadow-2xl">
        <div class="card-body p-8">
          <!-- Success Icon -->
          <div class="flex justify-center mb-4">
            <div class="rounded-full bg-green-100 p-3">
              <svg class="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>

          <!-- Header -->
          <h3 class="text-xl font-semibold text-gray-900 text-center mb-2">
            Login Verified!
          </h3>
          <p class="text-sm text-gray-600 text-center mb-6">
            Want faster logins with text messages?<br>
            Add a mobile number for SMS verification.
          </p>

          <!-- Mobile Number Input Form -->
          <form [formGroup]="mobileRegistrationForm" (ngSubmit)="onAddMobileNumber()" class="mb-6">
            <div class="mb-4">
              <label for="phoneNumber" class="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                formControlName="phoneNumber"
                class="input w-full"
                placeholder="Enter 10-digit mobile number"
                maxlength="10"
                pattern="[0-9]{10}"
              />
              @if (mobileRegistrationForm.get('phoneNumber')?.invalid && mobileRegistrationForm.get('phoneNumber')?.touched) {
                <span class="text-xs text-red-600 mt-1 block">
                  Please enter a valid 10-digit mobile number
                </span>
              }
            </div>

            <!-- Action Buttons -->
            <div class="flex flex-col gap-3">
              <button
                type="submit"
                class="btn btn-primary w-full"
                [disabled]="registeringMobile || mobileRegistrationForm.invalid"
              >
                @if (registeringMobile) {
                  <span>Adding Mobile Number...</span>
                } @else {
                  <span>Add Mobile Number</span>
                }
              </button>
              
              <button
                type="button"
                class="btn btn-light w-full"
                (click)="onCloseMobilePrompt()"
                [disabled]="registeringMobile"
              >
                Not Now
              </button>
            </div>
          </form>

          <!-- Benefits -->
          <div class="border-t pt-4">
            <p class="text-xs text-gray-500 text-center">
              <strong>Benefits:</strong> Faster login codes, backup verification method, and account alerts
            </p>
          </div>
        </div>
      </div>
    </div>
  }
</div>
